import React, { useEffect, useState } from 'react'
import styled from 'styled-components/macro'
import {
  HARBOUR_HOW_IT_WORKS_DISMISS,
  HARBOUR_HOW_IT_WORKS_LINK,
  HARBOUR_HOW_IT_WORKS_STEPS,
  HARBOUR_HOW_IT_WORKS_TITLE,
} from 'src/constants/harbour'
import {
  isHarbourHowItWorksDismissed,
  setHarbourHowItWorksDismissed,
} from 'src/utils/harbourHowItWorks'

/**
 * First-visit strip — Don’t show again. Not a permanent banner.
 * A small link reopens it when they need a reminder.
 */
const HarbourHowItWorks = ({ accountId } = {}) => {
  const [open, setOpen] = useState(
    () => !isHarbourHowItWorksDismissed(accountId),
  )

  useEffect(() => {
    setOpen(!isHarbourHowItWorksDismissed(accountId))
  }, [accountId])

  const handleDontShowAgain = () => {
    setHarbourHowItWorksDismissed(accountId, true)
    setOpen(false)
  }

  if (!open) {
    return (
      <ReopenRow>
        <Reopen
          type='button'
          onClick={() => setOpen(true)}
          data-testid='harbour-how-it-works-link'
        >
          {HARBOUR_HOW_IT_WORKS_LINK}
        </Reopen>
      </ReopenRow>
    )
  }

  return (
    <Strip data-testid='harbour-how-it-works' data-layout='compact'>
      <Head>
        <Title>{HARBOUR_HOW_IT_WORKS_TITLE}</Title>
        <Close
          type='button'
          onClick={() => setOpen(false)}
          data-testid='harbour-how-it-works-close'
        >
          Close
        </Close>
      </Head>
      <Steps>
        {HARBOUR_HOW_IT_WORKS_STEPS.map((step, index) => (
          <Step
            key={step.title}
            data-testid={`harbour-how-it-works-step-${index + 1}`}
          >
            <StepNum>{index + 1}</StepNum>
            <div>
              <StepTitle>{step.title}</StepTitle>
              <StepBody>{step.body}</StepBody>
            </div>
          </Step>
        ))}
      </Steps>
      <Dismiss
        type='button'
        onClick={handleDontShowAgain}
        data-testid='harbour-how-it-works-dismiss'
      >
        {HARBOUR_HOW_IT_WORKS_DISMISS}
      </Dismiss>
    </Strip>
  )
}

const Strip = styled.aside`
  padding: 6px 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background: #fbfbf8;
`

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
`

const Title = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const Close = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const Steps = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;

  @media (max-width: 52rem) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const Step = styled.li`
  display: flex;
  gap: 6px;
  align-items: flex-start;
  min-width: 0;
  padding: 6px 8px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
`

const StepNum = styled.span`
  flex: 0 0 1rem;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.35;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
`

const StepTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const StepBody = styled.p`
  margin: 1px 0 0;
  font-size: 11px;
  line-height: 1.25;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const Dismiss = styled.button`
  margin-top: 6px;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  text-decoration: underline;
`

const ReopenRow = styled.div`
  max-width: 40rem;
`

const Reopen = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  text-decoration: underline;
`

export default HarbourHowItWorks
